import { useState } from "react";
import { Link } from "react-router-dom";
import { Stack, Typography } from "@mui/material";

const SERIF = "'Newsreader', Georgia, serif";

/** Design-faithful input: uppercase micro-label, icon, 52px field, 3px radius. */
export function AuthField({
    label,
    labelRight,
    icon,
    endSlot,
    ...inputProps
}) {
    const [focused, setFocused] = useState(false);

    return (
        <Stack sx={{ gap: "8px" }}>
            <Stack
                sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                }}
            >
                <Typography
                    component="label"
                    sx={{
                        fontSize: "12.5px",
                        fontWeight: 600,
                        letterSpacing: "0.8px",
                        color: "#504D48",
                    }}
                >
                    {label}
                </Typography>
                {labelRight}
            </Stack>
            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "12px",
                    border: `1px solid ${focused ? "#171715" : "#C9C6C0"}`,
                    boxShadow: focused ? "0 0 0 3px #EAE8E3" : "none",
                    background: "#FFFFFF",
                    height: 52,
                    px: "16px",
                    borderRadius: "3px",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                }}
            >
                {icon}
                <Stack
                    component="input"
                    {...inputProps}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    sx={{
                        flexGrow: 1,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: 15,
                        color: "#171715",
                        fontFamily: "inherit",
                        "&::placeholder": { color: "#B8B5AF" },
                    }}
                />
                {endSlot}
            </Stack>
        </Stack>
    );
}

export const MailIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 20 20"
        fill="none"
        stroke="#8B8781"
        strokeWidth="1.5"
    >
        <rect x="2" y="4" width="16" height="12" rx="1.5" />
        <path d="m2.5 5.5 7.5 6 7.5-6" />
    </svg>
);

export const LockIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 20 20"
        fill="none"
        stroke="#8B8781"
        strokeWidth="1.5"
    >
        <rect x="4" y="9" width="12" height="8" rx="1.5" />
        <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
    </svg>
);

export const PersonIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 20 20"
        fill="none"
        stroke="#8B8781"
        strokeWidth="1.5"
    >
        <circle cx="10" cy="7" r="3.2" />
        <path d="M3.5 17c.7-3 3.2-4.6 6.5-4.6s5.8 1.6 6.5 4.6" />
    </svg>
);

export const BuildingIcon = () => (
    <svg
        width="17"
        height="17"
        viewBox="0 0 20 20"
        fill="none"
        stroke="#8B8781"
        strokeWidth="1.5"
    >
        <rect x="4" y="3" width="12" height="14" rx="1" />
        <path d="M7.5 6.5h1.5M11 6.5h1.5M7.5 9.5h1.5M11 9.5h1.5M8.5 17v-3h3v3" />
    </svg>
);

/** Solid-black primary submit button used across the auth screens. */
export function AuthSubmit({ children, loading, ...props }) {
    return (
        <Stack
            component="button"
            type="submit"
            disabled={loading}
            {...props}
            sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                background: "#171715",
                color: "#FAFAF9",
                height: 54,
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: "0.3px",
                borderRadius: "3px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                opacity: loading ? 0.7 : 1,
                "&:hover": { background: "#2B2A27" },
            }}
        >
            {children}
            <svg
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
                stroke="#FAFAF9"
                strokeWidth="1.6"
            >
                <path d="M2 8h11M9 3.5 13.5 8 9 12.5" />
            </svg>
        </Stack>
    );
}

/** The small pipeline motif at the foot of the brand panel (Login.dc.html). */
export function PipelineMotif() {
    return (
        <svg
            width="100%"
            height="120"
            viewBox="0 0 500 120"
            fill="none"
            style={{ opacity: 0.9, maxWidth: 500 }}
        >
            <defs>
                <marker
                    id="lah"
                    markerWidth="8"
                    markerHeight="8"
                    refX="7"
                    refY="4"
                    orient="auto"
                >
                    <path
                        d="M0 0.5 7 4 0 7.5"
                        fill="none"
                        stroke="#4CAF87"
                        strokeWidth="1.4"
                    />
                </marker>
                <marker
                    id="lah2"
                    markerWidth="8"
                    markerHeight="8"
                    refX="7"
                    refY="4"
                    orient="auto"
                >
                    <path
                        d="M0 0.5 7 4 0 7.5"
                        fill="none"
                        stroke="#57544F"
                        strokeWidth="1.4"
                    />
                </marker>
            </defs>
            <rect
                x="1"
                y="36"
                width="130"
                height="48"
                rx="5"
                fill="none"
                stroke="#57544F"
            />
            <rect
                x="185"
                y="36"
                width="130"
                height="48"
                rx="5"
                fill="none"
                stroke="#57544F"
            />
            <rect
                x="369"
                y="36"
                width="130"
                height="48"
                rx="5"
                fill="none"
                stroke="#4CAF87"
            />
            <path
                d="M131 60 H177"
                stroke="#57544F"
                strokeWidth="1.4"
                markerEnd="url(#lah2)"
            />
            <path
                d="M315 60 H361"
                stroke="#4CAF87"
                strokeWidth="1.6"
                markerEnd="url(#lah)"
            />
            <text
                x="19"
                y="65"
                fontFamily="Hanken Grotesk, sans-serif"
                fontSize="13"
                fontWeight="600"
                fill="#B8B5AF"
            >
                Enquiry
            </text>
            <text
                x="203"
                y="65"
                fontFamily="Hanken Grotesk, sans-serif"
                fontSize="13"
                fontWeight="600"
                fill="#B8B5AF"
            >
                Qualified
            </text>
            <text
                x="387"
                y="65"
                fontFamily="Hanken Grotesk, sans-serif"
                fontSize="13"
                fontWeight="600"
                fill="#4CAF87"
            >
                Won
            </text>
        </svg>
    );
}

/** Shield footnote used by the OTP step's brand panel (Verify.dc.html). */
export function ShieldNote() {
    return (
        <Stack
            sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: "14px",
                color: "#8B8781",
                fontSize: "13.5px",
            }}
        >
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="#4CAF87"
                strokeWidth="1.5"
            >
                <path d="M10 2 3.5 4.5v5c0 4 2.8 6.9 6.5 8.5 3.7-1.6 6.5-4.5 6.5-8.5v-5z" />
                <path d="M7 10l2 2 4-4.5" />
            </svg>
            Codes expire in 10 minutes and work exactly once.
        </Stack>
    );
}

/**
 * Split-screen auth shell (Login.dc.html / Verify.dc.html): black brand
 * panel on the left, form content on the right.
 */
export default function AuthShell({ headline, sub, bottomSlot, children }) {
    return (
        <Stack
            sx={{
                width: "100vw",
                minHeight: "100vh",
                flexDirection: "row",
                background: "#FAFAF9",
                color: "#171715",
                fontFamily:
                    "'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif",
            }}
        >
            {/* LEFT BRAND PANEL */}
            <Stack
                sx={{
                    width: { xs: 0, md: "43%" },
                    maxWidth: 620,
                    display: { xs: "none", md: "flex" },
                    flexShrink: 0,
                    background: "#171715",
                    color: "#FAFAF9",
                    justifyContent: "space-between",
                    p: "56px 60px",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Link to="/">
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "baseline",
                            gap: "10px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: SERIF,
                                fontSize: "26px",
                                fontWeight: 500,
                                letterSpacing: "-0.5px",
                                color: "#FAFAF9",
                            }}
                        >
                            tessera
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "9px",
                                letterSpacing: "2.5px",
                                color: "#8B8781",
                                fontWeight: 600,
                            }}
                        >
                            CRM PLATFORM
                        </Typography>
                    </Stack>
                </Link>

                <Stack sx={{ gap: "24px" }}>
                    <Typography
                        sx={{
                            fontFamily: SERIF,
                            fontSize: "46px",
                            lineHeight: 1.15,
                            letterSpacing: "-1px",
                            color: "#FAFAF9",
                        }}
                    >
                        {headline}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "15.5px",
                            lineHeight: 1.7,
                            color: "#A8A49D",
                            maxWidth: 420,
                        }}
                    >
                        {sub}
                    </Typography>
                </Stack>

                {bottomSlot ?? <PipelineMotif />}
            </Stack>

            {/* RIGHT FORM PANEL */}
            <Stack
                sx={{
                    flexGrow: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    p: { xs: 3, md: 8 },
                }}
            >
                {children}
            </Stack>
        </Stack>
    );
}
